import { useCallback, useEffect, useRef, useState } from 'react';

import type { AdvisorV2ThreadMessageRow, AdvisorV2ThreadMessagesResponse } from './create-advisor-v2-thread-api';
import type { SupportSessionData } from './support-session';

/** Matches agent ``pending_report_jobs`` throttle in ``deliver_pending_reports_for_thread``. */
export const ADVISOR_PENDING_REPORT_POLL_INTERVAL_MS = 30_000;

/**
 * Tighter cadence while a human agent owns the thread.
 *
 * Their replies arrive from DevRev while the customer sits still, and nothing pushes them to the
 * client: without a poll the transcript only catches up when the customer sends a message or
 * reopens the thread, which is not how a support conversation behaves. 30s reads as broken in a
 * live exchange; 10s is the slowest that still feels like a chat.
 *
 * The cost is one ``GET …/messages`` per open panel per tick, only while a session is live. The
 * agent answers it with an indexed existence check for undelivered report jobs before doing any
 * delivery work, so a tick with nothing pending is a handful of queries.
 */
export const ADVISOR_SUPPORT_SESSION_POLL_INTERVAL_MS = 10_000;

export type UseAdvisorPendingReportDeliveryPollOptions = {
  enabled: boolean;
  threadId: string | null | undefined;
  /**
   * True only when ``threadId`` is known to exist in agent Postgres (history select, successful
   * stream settle, or ``sessionStorage`` restore of that ready flag). Virgin client-minted ids stay
   * false so this poll does not ``GET …/messages`` before the first stream (those 404s inflate
   * api-agent 4xx rate).
   */
  threadServerReady: boolean;
  /** Lane for ``GET /v2/chat/threads/{id}/messages?use_case=…``. */
  resolveUseCase: () => string;
  /** Bump when a stream finishes (``submitted``/``streaming`` → ``ready``). */
  streamIdleToken: number;
  chatStatus: 'ready' | 'streaming' | 'submitted' | 'error';
  fetchThreadMessages: (threadId: string, useCase: string) => Promise<AdvisorV2ThreadMessagesResponse>;
  /**
   * Receives the refreshed transcript plus the thread's support sessions. The sessions ride along
   * because this sync is the only thing that runs after a ticket resolves: the HUMAN-mode stream
   * part stops at the close and so cannot report it.
   */
  onThreadMessages: (rows: AdvisorV2ThreadMessageRow[], supportSessions: SupportSessionData[]) => void;
  /** Called when messages GET indicates the thread is missing (clear ``threadServerReady``). */
  onThreadNotFound?: () => void;
  /**
   * True while a support session on this thread is REQUESTED or OPEN, i.e. a human agent owns it.
   * Keeps the poll running with no pending report jobs, so their replies land without the customer
   * having to type or reload.
   */
  supportSessionLive?: boolean;
  intervalMs?: number;
  supportIntervalMs?: number;
};

/** True when a messages fetch failed because the thread does not exist server-side. */
export function isAdvisorThreadNotFoundError(err: unknown): boolean {
  if (!(err instanceof Error)) {
    return false;
  }
  const msg = err.message.toLowerCase();
  // Match transport snippets from createAdvisorV2ThreadApi (`HTTP ${status}` / body text) —
  // not a bare ``404`` substring (unrelated failures must not clear ``threadServerReady``).
  return msg.includes('thread not found') || msg.includes('http 404');
}

/**
 * Polls thread messages while there is a reason to: undelivered report jobs, or a live support
 * session whose human agent can reply at any time. Each fetch runs server-side report delivery,
 * then refreshes the in-memory transcript and the thread's support sessions.
 * Does not fetch until ``threadServerReady`` — virgin client UUIDs have no DB row and no jobs.
 */
export function useAdvisorPendingReportDeliveryPoll(
  options: UseAdvisorPendingReportDeliveryPollOptions
): { pendingReportJobs: number } {
  const {
    enabled,
    threadId,
    threadServerReady,
    resolveUseCase,
    streamIdleToken,
    chatStatus,
    fetchThreadMessages,
    onThreadMessages,
    onThreadNotFound,
    supportSessionLive = false,
    intervalMs = ADVISOR_PENDING_REPORT_POLL_INTERVAL_MS,
    supportIntervalMs = ADVISOR_SUPPORT_SESSION_POLL_INTERVAL_MS,
  } = options;

  const [pendingReportJobs, setPendingReportJobs] = useState(0);
  const syncInFlightRef = useRef(false);
  const syncQueuedRef = useRef(false);
  const currentThreadIdRef = useRef<string | null>(threadId?.trim() ?? null);
  currentThreadIdRef.current = threadId?.trim() ?? null;
  const chatStatusRef = useRef(chatStatus);
  chatStatusRef.current = chatStatus;
  const threadServerReadyRef = useRef(threadServerReady);
  threadServerReadyRef.current = threadServerReady;

  const resolveUseCaseRef = useRef(resolveUseCase);
  resolveUseCaseRef.current = resolveUseCase;
  const fetchRef = useRef(fetchThreadMessages);
  fetchRef.current = fetchThreadMessages;
  const onMessagesRef = useRef(onThreadMessages);
  onMessagesRef.current = onThreadMessages;
  const onThreadNotFoundRef = useRef(onThreadNotFound);
  onThreadNotFoundRef.current = onThreadNotFound;

  const syncFromServer = useCallback(async () => {
    const tid = currentThreadIdRef.current?.trim();
    if (!enabled || !tid || !threadServerReadyRef.current || chatStatusRef.current !== 'ready') {
      return;
    }
    if (syncInFlightRef.current) {
      syncQueuedRef.current = true;
      return;
    }
    syncInFlightRef.current = true;
    try {
      const lane = resolveUseCaseRef.current().trim();
      if (!lane) {
        return;
      }
      const {
        messages,
        pendingReportJobs: pending,
        supportSessions,
      } = await fetchRef.current(tid, lane);
      if (currentThreadIdRef.current !== tid || chatStatusRef.current !== 'ready') {
        return;
      }
      onMessagesRef.current(messages, supportSessions ?? []);
      setPendingReportJobs(Math.max(0, pending));
    } catch (err: unknown) {
      // Ignore stale in-flight failures after a thread switch (same guard as the success path).
      if (currentThreadIdRef.current !== tid) {
        return;
      }
      if (isAdvisorThreadNotFoundError(err)) {
        setPendingReportJobs(0);
        onThreadNotFoundRef.current?.();
      } else if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('[Kin] pending report delivery sync failed', { threadId: tid, err });
      }
    } finally {
      syncInFlightRef.current = false;
      if (syncQueuedRef.current) {
        syncQueuedRef.current = false;
        void syncFromServer();
      }
    }
  }, [enabled]);

  useEffect(() => {
    setPendingReportJobs(0);
  }, [threadId]);

  useEffect(() => {
    if (!enabled || !threadId?.trim() || !threadServerReady) {
      setPendingReportJobs(0);
      return;
    }
    // Guard only — do not list ``chatStatus`` in deps (would double-fire with ``streamIdleToken``
    // on the same settle). Stream end always bumps ``streamIdleToken``, including when mark-ready
    // is skipped after a mid-stream thread switch, so this effect retries once status is ready.
    if (chatStatus !== 'ready') {
      return;
    }
    void syncFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chatStatus is a guard; see above
  }, [enabled, threadId, threadServerReady, streamIdleToken, syncFromServer]);

  useEffect(() => {
    // Two reasons to keep ticking: reports still being delivered, or a human agent who can reply
    // at any moment. The support cadence wins when both apply, since a live conversation is the
    // one a customer is watching.
    const wanted = pendingReportJobs > 0 || supportSessionLive;
    if (!enabled || !threadId?.trim() || !threadServerReady || !wanted || chatStatus !== 'ready') {
      return;
    }
    const id = window.setInterval(
      () => {
        void syncFromServer();
      },
      supportSessionLive ? supportIntervalMs : intervalMs
    );
    return () => window.clearInterval(id);
  }, [
    enabled,
    threadId,
    threadServerReady,
    pendingReportJobs,
    supportSessionLive,
    chatStatus,
    intervalMs,
    supportIntervalMs,
    syncFromServer,
  ]);

  return { pendingReportJobs };
}
